/**
 * All the sound in the game, synthesised.
 *
 * There is no dialogue and there are no words, so sound carries the entire feedback layer: what the
 * engine is doing, what she is driving on, that she has got the thing. Every noise here is generated
 * with oscillators and filtered noise rather than loaded from a file - which means no audio assets to
 * source or licence, nothing to download, and an engine note that can follow the wheels continuously
 * instead of looping.
 *
 * If a sample pack is installed (see samples.ts) the one-shots play from it instead, because a recorded
 * chime beats an oscillator every time. The synth is the floor, not the ceiling - and with no pack
 * present the game still makes every sound it needs.
 *
 * Two hard constraints shape the implementation:
 *
 *  - **iOS will not start audio without a user gesture.** Nothing here creates an AudioContext until
 *    `unlock()` is called from a real touch - the boot screen's big paw.
 *  - **It must never throw.** A tablet with audio disabled, or a browser that refuses the context, has
 *    to give a silent game rather than a broken one. Every entry point is guarded.
 */

import { loadSamplePack, type SampleName } from './samples.js';

export type SurfaceSound = 'road' | 'grass' | 'mud' | 'sand' | 'ice' | 'water';

interface Bus {
  readonly gain: GainNode;
}

/** One filtered noise bed: the rain's hiss or the wind's roll. */
interface WeatherBed {
  readonly source: AudioBufferSourceNode;
  readonly filter: BiquadFilterNode;
  readonly gain: GainNode;
}

export class AudioDirector {
  private context: AudioContext | null = null;
  private music: Bus | null = null;
  private sfx: Bus | null = null;

  private engine: {
    readonly osc: OscillatorNode;
    readonly sub: OscillatorNode;
    readonly filter: BiquadFilterNode;
    readonly gain: GainNode;
  } | null = null;

  private tyres: {
    readonly source: AudioBufferSourceNode;
    readonly filter: BiquadFilterNode;
    readonly gain: GainNode;
  } | null = null;

  /**
   * The last value asked of each bed, so an unchanged one is not asked for again.
   *
   * `setEngine` and `setSurface` are called on every frame of a drive - six `setTargetAtTime` calls
   * between them, each an event scheduled on an audio-thread timeline, for the whole leg and including
   * the long stretches where nothing about the sound is changing: parked, or holding a steady speed on
   * one surface. A repeat is not merely cheap-but-wasteful, it is a no-op by definition, because the ramp
   * already running is heading for that exact value.
   *
   * NaN to start, so the first call after a bed is built always writes.
   */
  private engineTargets = { base: NaN, filter: NaN, gain: NaN };
  private tyreTargets = { frequency: NaN, gain: NaN };

  /**
   * The siren, as either a pair of oscillators or a looping sample - whichever the pack gives us.
   *
   * `source` is set on the sample path and `voices` on the synth one, never both, and `setSiren` drives
   * whichever is there.
   */
  private siren: {
    readonly gain: GainNode;
    readonly filter: BiquadFilterNode;
    readonly voices: readonly OscillatorNode[];
    readonly source: AudioBufferSourceNode | null;
  } | null = null;

  /**
   * The weather's two beds, or null on a clear day.
   *
   * Both or neither: they are built together on the first call that asks for either, because a game that
   * starts raining halfway through a leg does not exist - the weather is one decision per leg (`weather.ts`).
   */
  private weather: { readonly rain: WeatherBed; readonly wind: WeatherBed } | null = null;

  private noiseBuffer: AudioBuffer | null = null;
  private enabled = true;
  /** Whatever the installed pack contains. Empty is the normal case. */
  private samples: Partial<Record<SampleName, AudioBuffer>> = {};

  get ready(): boolean {
    return this.context !== null && this.context.state === 'running';
  }

  /**
   * Create (or resume) the audio context. Must be called from inside a user-gesture handler; the boot
   * screen's start button exists partly for this reason.
   */
  async unlock(): Promise<void> {
    if (!this.enabled) return;
    try {
      this.context ??= new AudioContext();
      if (this.context.state === 'suspended') await this.context.resume();

      if (this.music === null || this.sfx === null) {
        /*
         * A limiter on the way out.
         *
         * Tablet speakers are small, quiet and have essentially no bass, so everything needs to be
         * louder than it would be on headphones - and once things are louder they need something to
         * stop the engine, the tyres and a sting all landing at once from clipping.
         */
        const limiter = this.context.createDynamicsCompressor();
        limiter.threshold.value = -12;
        limiter.knee.value = 12;
        limiter.ratio.value = 8;
        limiter.attack.value = 0.004;
        limiter.release.value = 0.18;
        limiter.connect(this.context.destination);

        this.music = { gain: this.context.createGain() };
        this.sfx = { gain: this.context.createGain() };
        this.music.gain.connect(limiter);
        this.sfx.gain.connect(limiter);
      }

      // Fire and forget: whatever the pack has replaces the synth as it arrives, and the game is fully
      // playable in the meantime.
      const context = this.context;
      void loadSamplePack(context).then((pack) => {
        this.samples = pack;
      });
    } catch (error) {
      // A device that will not give us audio gets a silent game, not a broken one.
      console.warn('[pawp] audio unavailable; continuing without sound', error);
      this.enabled = false;
      this.context = null;
    }
  }

  setVolumes(musicVolume: number, sfxVolume: number): void {
    if (this.music !== null) this.music.gain.gain.value = clamp01(musicVolume);
    if (this.sfx !== null) this.sfx.gain.gain.value = clamp01(sfxVolume);
  }

  /* ---------------------------------------------------------------- engine */

  startEngine(): void {
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null || this.engine !== null) return;

    // Two detuned saws through a lowpass: a small, buzzy, toy-ish motor rather than a real one.
    const osc = context.createOscillator();
    const sub = context.createOscillator();
    osc.type = 'sawtooth';
    sub.type = 'square';

    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    // Opens far higher than the first version's 700Hz. A tablet speaker reproduces almost nothing below
    // a couple of hundred Hz, so a motor voiced in the bass is a motor she cannot hear at all.
    filter.frequency.value = 1400;
    filter.Q.value = 2;

    const gain = context.createGain();
    gain.gain.value = 0;

    osc.connect(filter);
    sub.connect(filter);
    filter.connect(gain);
    gain.connect(sfx.gain);
    osc.start();
    sub.start();

    this.engine = { osc, sub, filter, gain };
    // A new motor knows nothing about what the last one was doing.
    this.engineTargets = { base: NaN, filter: NaN, gain: NaN };
  }

  /**
   * `speed` and `throttle` are both 0..1. Pitch follows speed so the motor rises as she gets going;
   * volume follows both - throttle, so releasing the pedal audibly lets off, which is the only feedback
   * that the pedal did anything when the car is already rolling, and speed, so a parked car is quiet.
   */
  setEngine(speed: number, throttle: number): void {
    const engine = this.engine;
    const context = this.context;
    if (engine === null || context === null) return;

    // A context suspended by the browser (backgrounding the tab, usually) makes the whole game silent.
    // Nudge it back rather than waiting for another gesture that may never come.
    if (context.state === 'suspended') void context.resume();

    const now = context.currentTime;
    /*
     * 120Hz idling to 360Hz flat out, not 58-188.
     *
     * The first version put the fundamental squarely in the range a tablet speaker cannot produce, so
     * the engine was inaudible and the only sounds she ever heard were the loud stings - which read as
     * "sound only works when I get home".
     */
    const base = 120 + clamp01(speed) * 240;
    // Ramps rather than jumps: setValueAtTime on every frame produces audible stepping.
    if (changed(this.engineTargets.base, base, HZ_EPSILON)) {
      this.engineTargets.base = base;
      engine.osc.frequency.setTargetAtTime(base, now, 0.06);
      engine.sub.frequency.setTargetAtTime(base / 2, now, 0.06);
    }
    const cutoff = 1100 + clamp01(speed) * 2400;
    if (changed(this.engineTargets.filter, cutoff, HZ_EPSILON)) {
      this.engineTargets.filter = cutoff;
      engine.filter.frequency.setTargetAtTime(cutoff, now, 0.08);
    }
    /*
     * The bed is tied to speed, not held flat: parked it is a near-silent 0.02, and it only reaches the
     * old 0.1 idle once the car is actually rolling. A stationary car humming at full idle level was the
     * loudest thing in a room where nothing was happening, and there is no feedback in it - she is not
     * moving and the pedal is not pressed, so the motor has nothing to tell her.
     *
     * Throttle still adds its own 0.2 on top, so pressing the pedal from a standstill is a bigger jump
     * than it was before, and flat out is as loud as it ever was.
     */
    const bed = 0.02 + clamp01(speed) * 0.08;
    const level = bed + clamp01(throttle) * 0.2;
    if (changed(this.engineTargets.gain, level, GAIN_EPSILON)) {
      this.engineTargets.gain = level;
      engine.gain.gain.setTargetAtTime(level, now, 0.1);
    }
  }

  stopEngine(): void {
    const engine = this.engine;
    if (engine === null) return;
    try {
      engine.osc.stop();
      engine.sub.stop();
      engine.gain.disconnect();
    } catch {
      // Already stopped; nothing to do.
    }
    this.engine = null;
  }

  /* ---------------------------------------------------------------- siren */

  /**
   * The siren behind the emergency lights.
   *
   * A bed rather than a one-shot, like the engine and the tyres: it runs for as long as she keeps the horn
   * held, and `setSiren` is called every frame with how loud it should be and which of its two tones it is
   * on. The *tone comes from the lights* rather than from a clock in here, which is the whole trick - the
   * wail and the flash cannot drift apart, and speeding the lights up speeds the siren up for nothing.
   *
   * A pack sample wins if there is one, exactly as it does for every other sound; a recorded siren already
   * wails, so on that path the tone is ignored and urgency nudges the playback rate instead.
   */
  startSiren(): void {
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null || this.siren !== null) return;

    const gain = context.createGain();
    gain.gain.value = 0;

    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = SIREN_FILTER_MIN_HZ;
    // Resonant, so it sings rather than buzzes - the same reason the horn's shaper has a Q.
    filter.Q.value = 5;

    filter.connect(gain);
    gain.connect(sfx.gain);

    const buffer = this.samples.siren;
    if (buffer !== undefined) {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(filter);
      source.start();
      this.siren = { gain, filter, voices: [], source };
      return;
    }

    /*
     * Two detuned sawtooths, the same construction as the horn's two-tone.
     *
     * Detuned against each other rather than in unison: the beating between them is most of what makes a
     * siren sound like a siren rather than an alarm clock. The pitch is set by `setSiren` from whichever
     * lamp is lit, so nothing here decides when it changes.
     */
    const voices = [context.createOscillator(), context.createOscillator()];
    for (const [index, osc] of voices.entries()) {
      osc.type = 'sawtooth';
      osc.frequency.value = SIREN_TONE_HZ[0] * (index === 0 ? 1 : SIREN_DETUNE);
      osc.connect(filter);
      osc.start();
    }
    this.siren = { gain, filter, voices, source: null };
  }

  /**
   * `level` is 0..1 - how far into the escalation she is. `tone` picks which of the two notes it wails on.
   *
   * Starts the bed on the first call that asks for anything, so the drive scene does not have to know
   * whether it has started one: a siren that only exists once she honks is one fewer oscillator running
   * for the whole of every drive she never touches the horn on.
   */
  setSiren(level: number, tone: 0 | 1): void {
    const wanted = clamp01(level);
    if (wanted <= 0 && this.siren === null) return;
    if (this.siren === null) this.startSiren();

    const siren = this.siren;
    const context = this.context;
    if (siren === null || context === null) return;
    if (context.state === 'suspended') void context.resume();

    const now = context.currentTime;
    // Under the horn's own 0.5, because this one is held rather than tapped and a held sound at honk
    // volume is a held sound she will ask to have turned off.
    siren.gain.gain.setTargetAtTime(wanted * SIREN_LEVEL, now, 0.05);
    /*
     * Opening the filter as it climbs is what makes escalating read as more *urgent* rather than merely
     * louder. Volume alone is the wrong axis: it says "closer", not "hurrying".
     */
    siren.filter.frequency.setTargetAtTime(
      SIREN_FILTER_MIN_HZ + wanted * (SIREN_FILTER_MAX_HZ - SIREN_FILTER_MIN_HZ),
      now,
      0.08,
    );

    if (siren.source !== null) {
      // A recorded siren has its own wail in it, so urgency only leans on the tape speed. Gently: past
      // about a fifth it stops being urgent and starts being a cartoon.
      siren.source.playbackRate.setTargetAtTime(1 + wanted * 0.2, now, 0.12);
      return;
    }

    const base = SIREN_TONE_HZ[tone];
    for (const [index, osc] of siren.voices.entries()) {
      // A fast target rather than a jump: an instant frequency change on a running saw is a click.
      osc.frequency.setTargetAtTime(base * (index === 0 ? 1 : SIREN_DETUNE), now, 0.015);
    }
  }

  stopSiren(): void {
    const siren = this.siren;
    const context = this.context;
    if (siren === null) return;
    this.siren = null;

    try {
      /*
       * A few milliseconds of fade before the oscillators stop.
       *
       * Cutting a running sawtooth stack dead leaves the waveform at whatever value it happened to be at,
       * which is a click - and this is stopped mid-wail every time she leaves the screen or backgrounds
       * the tab, which is far more often than the engine ever is.
       */
      const now = context?.currentTime ?? 0;
      siren.gain.gain.cancelScheduledValues(now);
      siren.gain.gain.setTargetAtTime(0, now, SIREN_STOP_FADE_S / 3);
      const at = now + SIREN_STOP_FADE_S;
      for (const osc of siren.voices) osc.stop(at);
      siren.source?.stop(at);
    } catch {
      // Already stopped; nothing to do.
    }
  }

  /* ---------------------------------------------------------------- weather */

  /**
   * The weather, as two noise beds: rain up high, wind down low.
   *
   * Beds rather than one-shots, for the same reason the engine and the tyres are - and this is the one sound
   * in the game she hears with her finger off every pedal, which is exactly why it is worth having. A parked
   * car in the rain is silent otherwise, and silence says the game has stopped.
   *
   * The *shape* comes from the caller, as it does for the siren: `src/drive/weather.ts` owns the gust, so the
   * wind swelling and the rain slanting are one number rather than two clocks that can drift apart. The gust
   * is computed from seconds, not frames, because the tablet she has runs at thirty.
   *
   * Both beds are filtered noise from the same generated buffer, and neither is built until something asks
   * for it - a clear day costs nothing at all.
   */
  setWeather(rainLevel: number, windLevel: number): void {
    const rain = clamp01(rainLevel);
    const wind = clamp01(windLevel);
    if (rain <= 0 && wind <= 0 && this.weather === null) return;

    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;
    if (context.state === 'suspended') void context.resume();

    if (this.weather === null) {
      const buffer = this.noise();
      if (buffer === null) return;

      const build = (type: BiquadFilterType, frequency: number): WeatherBed | null => {
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.loop = true;

        const filter = context.createBiquadFilter();
        filter.type = type;
        filter.frequency.value = frequency;

        const gain = context.createGain();
        gain.gain.value = 0;

        source.connect(filter);
        filter.connect(gain);
        gain.connect(sfx.gain);
        source.start();
        return { source, filter, gain };
      };

      // Bright hiss for the rain, a low roll for the wind: two ends of the same noise, which is what makes
      // them sit together rather than mask each other.
      const rainBed = build('highpass', 2200);
      const windBed = build('lowpass', 420);
      if (rainBed === null || windBed === null) return;
      this.weather = { rain: rainBed, wind: windBed };
    }

    const now = context.currentTime;
    // Slow ramps: weather has no attack, and a gust that arrives instantly reads as a fault in the speaker.
    this.weather.rain.gain.gain.setTargetAtTime(rain * RAIN_LEVEL, now, 0.4);
    this.weather.wind.gain.gain.setTargetAtTime(wind * WIND_LEVEL, now, 0.6);
    // The wind opens up as it gusts, which is most of what says "harder" rather than "louder".
    this.weather.wind.filter.frequency.setTargetAtTime(320 + wind * 520, now, 0.6);
  }

  stopWeather(): void {
    const weather = this.weather;
    if (weather === null) return;
    this.weather = null;
    try {
      for (const bed of [weather.rain, weather.wind]) {
        bed.source.stop();
        bed.gain.disconnect();
      }
    } catch {
      // Already stopped.
    }
  }

  /* ---------------------------------------------------------------- tyres */

  /** A filtered noise bed whose colour says what she is driving on. */
  setSurface(surface: SurfaceSound, speed: number): void {
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    if (this.tyres === null) {
      const source = context.createBufferSource();
      source.buffer = this.noise();
      source.loop = true;

      const filter = context.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 800;
      filter.Q.value = 0.8;

      const gain = context.createGain();
      gain.gain.value = 0;

      source.connect(filter);
      filter.connect(gain);
      gain.connect(sfx.gain);
      source.start();
      this.tyres = { source, filter, gain };
      this.tyreTargets = { frequency: NaN, gain: NaN };
    }

    const profile = TYRE_PROFILE[surface];
    const level = profile.gain * clamp01(speed);
    const now = context.currentTime;
    if (changed(this.tyreTargets.frequency, profile.frequency, HZ_EPSILON)) {
      this.tyreTargets.frequency = profile.frequency;
      this.tyres.filter.frequency.setTargetAtTime(profile.frequency, now, 0.12);
    }
    if (changed(this.tyreTargets.gain, level, GAIN_EPSILON)) {
      this.tyreTargets.gain = level;
      this.tyres.gain.gain.setTargetAtTime(level, now, 0.12);
    }
  }

  stopTyres(): void {
    const tyres = this.tyres;
    if (tyres === null) return;
    try {
      tyres.source.stop();
      tyres.gain.disconnect();
    } catch {
      // Already stopped.
    }
    this.tyres = null;
  }

  /* ---------------------------------------------------------------- stings */

  /** The moment of collection: a rising five-note run. Unmistakably "yes, that". */
  collect(): void {
    if (this.playSample('collect', 0.9)) return;
    this.arpeggio([523, 659, 784, 1047, 1319], 0.085, 'triangle', 0.3);
  }

  /** Arriving home with the thing. Bigger, and it resolves downward-then-up like a fanfare. */
  fanfare(): void {
    if (this.playSample('fanfare', 0.9)) return;
    this.arpeggio([392, 523, 659, 523, 784, 1047], 0.12, 'triangle', 0.34);
  }

  /**
   * A button, answering.
   *
   * The one sound in the game that is deliberately almost nothing: it plays on *every* press of every
   * menu button, dozens of times an evening, so anything with a note in it becomes a tune she is playing
   * by accident and anything long overlaps itself when she drums on a card. A wooden tock - one short
   * triangle blip with a breath of noise on the front - is the shortest thing that still says "that
   * worked", which is the whole job. The Garage was silent before this and read as broken.
   *
   * Quiet on purpose (0.16 against the horn's 0.5): she has to be able to hear the press *and* whatever
   * the press led to, and the second one is the payoff.
   */
  press(): void {
    if (this.playSample('press', 0.5)) return;
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    // Above the engine's range and below the collect chime's, so it sits in neither and cuts through both.
    this.blip(880, 0.05, 'triangle', 0.16);

    // The tap itself: a few milliseconds of bright noise, which is what makes it wood rather than a beep.
    const buffer = this.noise();
    if (buffer === null) return;
    const now = context.currentTime;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 2.4;

    const filter = context.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 2400;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.03);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(sfx.gain);
    source.start(now);
    source.stop(now + 0.04);
  }

  /** A soft two-note bark for the pup. */
  bark(): void {
    if (this.playSample('bark', 0.85)) return;
    this.blip(320, 0.09, 'square', 0.24);
    window.setTimeout(() => this.blip(260, 0.12, 'square', 0.2), 110);
  }

  /**
   * The horn. Affects nothing in the game, on purpose - and it is probably the button she will press
   * most, so it had better be worth pressing.
   *
   * A real two-tone air horn rather than two square blips: a pair of detuned sawtooth voices a fourth
   * apart, an octave-down body to give it weight, a resonant lowpass so it honks rather than buzzes, and
   * a breath of noise on the attack for the puff of air. It also sags in pitch as it releases, the way a
   * horn running out of pressure does.
   */
  horn(): void {
    if (this.playSample('horn', 0.9)) return;

    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    const now = context.currentTime;
    const duration = 0.46;

    const shaper = context.createBiquadFilter();
    shaper.type = 'lowpass';
    shaper.frequency.setValueAtTime(2600, now);
    shaper.Q.value = 6;

    const gain = context.createGain();
    // Fast but not instant: a hard start on a saw stack reads as a click rather than a honk.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.5, now + 0.02);
    gain.gain.setValueAtTime(0.5, now + duration - 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    shaper.connect(gain);
    gain.connect(sfx.gain);

    // Two voices a fourth apart, each very slightly detuned against itself: that beating is most of what
    // makes a horn sound like a horn rather than an organ.
    for (const [frequency, level, type] of [
      [330, 1, 'sawtooth'],
      [333, 0.9, 'sawtooth'],
      [440, 0.85, 'sawtooth'],
      [444, 0.8, 'sawtooth'],
      [165, 0.7, 'square'],
    ] as const) {
      const osc = context.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(frequency, now);
      // Sags as the pressure drops.
      osc.frequency.setValueAtTime(frequency, now + duration - 0.14);
      osc.frequency.linearRampToValueAtTime(frequency * 0.94, now + duration);

      const voice = context.createGain();
      voice.gain.value = level * 0.24;

      osc.connect(voice);
      voice.connect(shaper);
      osc.start(now);
      osc.stop(now + duration + 0.03);
    }

    // The puff of air at the start.
    const buffer = this.noise();
    if (buffer !== null) {
      const chuff = context.createBufferSource();
      chuff.buffer = buffer;

      const band = context.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = 1500;
      band.Q.value = 0.7;

      const chuffGain = context.createGain();
      chuffGain.gain.setValueAtTime(0.22, now);
      chuffGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);

      chuff.connect(band);
      band.connect(chuffGain);
      chuffGain.connect(sfx.gain);
      chuff.start(now);
      chuff.stop(now + 0.12);
    }
  }

  /** Hitting water. A quick noise burst that opens then closes. */
  splash(): void {
    if (this.playSample('splash', 0.9)) return;
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    const source = context.createBufferSource();
    source.buffer = this.noise();

    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(400, context.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2600, context.currentTime + 0.16);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.4, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.42);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(sfx.gain);
    source.start();
    source.stop(context.currentTime + 0.45);
  }

  /** A landing. `strength` 0..1 scales the thud. */
  thump(strength: number): void {
    const context = this.context;
    if (context === null) return;
    const level = clamp01(strength);
    if (level < 0.08) return;
    if (this.playSample('thump', level)) return;

    const osc = context.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(48, context.currentTime + 0.16);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.2 * level, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.28);

    osc.connect(gain);
    if (this.sfx !== null) gain.connect(this.sfx.gain);
    osc.start();
    osc.stop(context.currentTime + 0.3);
  }

  /** A soft crayon scritch when a region fills. */
  crayon(): void {
    if (this.playSample('crayon', 0.7)) return;
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    const source = context.createBufferSource();
    source.buffer = this.noise();
    source.playbackRate.value = 1.8;

    const filter = context.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1800;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.14, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.13);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(sfx.gain);
    source.start();
    source.stop(context.currentTime + 0.15);
  }

  dispose(): void {
    this.stopEngine();
    this.stopTyres();
    this.stopSiren();
    this.stopWeather();
    try {
      void this.context?.close();
    } catch {
      // Already closed.
    }
    this.context = null;
    this.music = null;
    this.sfx = null;
    this.noiseBuffer = null;
  }

  /* ---------------------------------------------------------------- helpers */

  /**
   * Play a one-shot from the installed pack. Returns false when there is no such sample, which is the
   * caller's signal to fall back to the synthesised version.
   */
  private playSample(name: SampleName, level: number): boolean {
    const context = this.context;
    const sfx = this.sfx;
    const buffer = this.samples[name];
    if (context === null || sfx === null || buffer === undefined) return false;

    const source = context.createBufferSource();
    source.buffer = buffer;

    const gain = context.createGain();
    gain.gain.value = clamp01(level);

    source.connect(gain);
    gain.connect(sfx.gain);
    source.start();
    return true;
  }

  private arpeggio(
    notes: readonly number[],
    spacing: number,
    type: OscillatorType,
    level: number,
  ): void {
    notes.forEach((note, index) => {
      this.blip(note, 0.22, type, level, index * spacing);
    });
  }

  private blip(
    frequency: number,
    duration: number,
    type: OscillatorType,
    level: number,
    delay = 0,
  ): void {
    const context = this.context;
    const sfx = this.sfx;
    if (context === null || sfx === null) return;

    const at = context.currentTime + delay;
    const osc = context.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, at);

    const gain = context.createGain();
    // A short attack rather than an instant one; a hard start on a sine is heard as a click.
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(level, at + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    osc.connect(gain);
    gain.connect(sfx.gain);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  /** One second of white noise, generated once and reused by every noise-based sound. */
  private noise(): AudioBuffer | null {
    const context = this.context;
    if (context === null) return null;
    if (this.noiseBuffer !== null) return this.noiseBuffer;

    const length = Math.floor(context.sampleRate);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    // Deterministic rather than Math.random: identical noise every session, one less thing to vary.
    let state = 22222;
    for (let i = 0; i < length; i++) {
      state = (state * 1103515245 + 12345) & 0x7fffffff;
      data[i] = (state / 0x3fffffff - 1) * 0.6;
    }
    this.noiseBuffer = buffer;
    return buffer;
  }
}

/*
 * The siren's two notes, in Hz.
 *
 * A minor third apart and up in the range a tablet speaker can actually reproduce - the same lesson the
 * engine note learned the hard way, where a motor voiced in the bass was a motor she never heard. Low
 * enough not to be shrill, because unlike the horn this one is held.
 */
const SIREN_TONE_HZ = [523, 622] as const;
/** How far the second voice sits off the first. The beating between them is what says "siren". */
const SIREN_DETUNE = 1.006;
/** Peak level, under the horn's 0.5: a held sound at honk volume is one she asks to have turned off. */
const SIREN_LEVEL = 0.28;

/**
 * How loud the weather gets at full strength.
 *
 * Under the tyres on the loudest surface (water, 0.28) and far under the engine, because this one is there for
 * the whole leg whether or not she is driving. It has to be audible when she stops and unnoticed when she
 * goes.
 */
const RAIN_LEVEL = 0.1;
const WIND_LEVEL = 0.075;
/** The filter sweep that makes escalating sound urgent rather than just louder. */
const SIREN_FILTER_MIN_HZ = 900;
const SIREN_FILTER_MAX_HZ = 3200;
/** Long enough to avoid a click on a stopped sawtooth, short enough to read as immediate. */
const SIREN_STOP_FADE_S = 0.06;

/** Bandpass centre and level per surface: gravelly low for mud, hissy high for water. */
const TYRE_PROFILE: Readonly<Record<SurfaceSound, { frequency: number; gain: number }>> = {
  road: { frequency: 1300, gain: 0.11 },
  grass: { frequency: 1000, gain: 0.13 },
  mud: { frequency: 620, gain: 0.26 },
  sand: { frequency: 900, gain: 0.2 },
  ice: { frequency: 2800, gain: 0.09 },
  water: { frequency: 2100, gain: 0.28 },
};

/**
 * Is this worth another timeline event?
 *
 * NaN on the left - the value a bed starts at - always counts as changed, because `NaN !== NaN` is not
 * something to rely on a reader noticing.
 */
function changed(last: number, next: number, epsilon: number): boolean {
  return Number.isNaN(last) || Math.abs(next - last) > epsilon;
}

/** Half a hertz is below anything audible as a change on these beds. */
const HZ_EPSILON = 0.5;
/** A thousandth of full scale, well under the resolution of a tablet speaker. */
const GAIN_EPSILON = 0.001;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** One director for the whole game; screens borrow it rather than making their own. */
export const audio = new AudioDirector();
